/**
 * Noticing that a peer is gone, rather than waiting to be told.
 *
 * A tab that closes politely announces itself and Trystero reports the peer leaving. A laptop that
 * closes its lid, a phone that walks out of range and a browser that is killed announce nothing,
 * and the room keeps listing a player who stopped existing minutes ago. A lobby that says
 * "Connected, 2 players" to somebody sitting alone is the worst kind of wrong: it looks fine.
 *
 * So the peer connection itself is watched. `disconnected` is WebRTC saying it has stopped hearing
 * from the far side and may yet recover, which is exactly `reconnecting`. `failed` and `closed` are
 * final, and there is no reason to keep the peer after either.
 */

/** How long a peer may stay `disconnected` before it is treated as gone. */
const RECOVERY_GRACE_MS = 8000;

export interface PeerHealthHandlers {
  /** The connection is quiet and may come back. */
  onUnstable(): void;
  /** It came back. */
  onStable(): void;
  /** It is gone, with a reason worth logging. */
  onLost(reason: string): void;
}

export interface PeerHealthWatch {
  stop(): void;
}

export function watchPeerHealth(
  connection: RTCPeerConnection,
  handlers: PeerHealthHandlers,
): PeerHealthWatch {
  let grace: ReturnType<typeof setTimeout> | undefined;
  let unstable = false;
  let stopped = false;

  const clearGrace = (): void => {
    clearTimeout(grace);
    grace = undefined;
  };

  const onChange = (): void => {
    if (stopped) return;

    const state = connection.connectionState;
    if (state === 'failed' || state === 'closed') {
      clearGrace();
      handlers.onLost(`the connection ${state}`);
      return;
    }

    if (state === 'disconnected') {
      if (unstable) return;
      unstable = true;
      handlers.onUnstable();
      grace = setTimeout(() => {
        handlers.onLost('the peer stopped answering');
      }, RECOVERY_GRACE_MS);
      return;
    }

    if (state === 'connected' && unstable) {
      unstable = false;
      clearGrace();
      handlers.onStable();
    }
  };

  connection.addEventListener('connectionstatechange', onChange);
  onChange();

  return {
    stop(): void {
      stopped = true;
      clearGrace();
      connection.removeEventListener('connectionstatechange', onChange);
    },
  };
}
